import { CheqEssentialsLogo } from '@clickcease/cc-client-components-lib'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { useBMStore } from '../../../../../stores/useBMStore'

import { ProductType } from '../../../../../types/federatedContext'
import { PLAN_TYPES } from '../../../../../types/plans'
import { PRODUCT_TYPES } from '../../../../../types/ProductTypes'

import {
  PDFHeaderTitle,
  PDFHeaderTitleSection,
  PDFHeaderDateSection,
  PDFHeaderWrapper,
  PDFDomainsSection,
  LogoWrapper,
} from './PDFHeader.styled'

interface PDFHeaderProps {
  locale?: string
  accountLogoDownloadLink?: string | undefined
  userPlans?: ProductType[]
}

export const PDFHeader: React.FC<PDFHeaderProps> = ({
  locale,
  userPlans,
  accountLogoDownloadLink,
}) => {
  const { t } = useTranslation('botMitigation')
  const dateRange = useBMStore((state) => state.dateRange)
  const chosenDomains = useBMStore((state) => state.chosenDomains)

  const isWhitelabelEligibleByPlan = userPlans?.some((plan) => {
    return (
      plan.product.toUpperCase() === PRODUCT_TYPES.BOT_ZAPPING.toUpperCase() &&
      plan.type != PLAN_TYPES.CORE
    )
  })

  const showWhitelabelLogo = isWhitelabelEligibleByPlan && accountLogoDownloadLink

  const renderLogo = () => {
    if (showWhitelabelLogo) {
      return (
        <img
          src={accountLogoDownloadLink}
          style={{ maxWidth: '150px', maxHeight: '120px' }}
          alt="whitelabel report logo"
        />
      )
    } else {
      return <CheqEssentialsLogo width="250px" />
    }
  }

  const formatDateRange = (fromDate: string, toDate: string, locale?: string): string => {
    const currentLocale = locale || 'en-US'

    const from = new Date(fromDate)
    const to = new Date(toDate)

    const sameYear = from.getFullYear() === to.getFullYear()

    const sameMonth = sameYear && from.getMonth() === to.getMonth()

    const fromMonth = from.toLocaleString(currentLocale, { month: 'short' })
    const toMonth = to.toLocaleString(currentLocale, { month: 'short' })

    const fromDay = from.getDate()
    const toDay = to.getDate()

    const year = to.getFullYear()

    if (sameMonth) {
      return `${fromMonth} ${fromDay} - ${toDay}, ${year}`
    } else {
      return `${fromMonth} ${fromDay} - ${toMonth} ${toDay}, ${year}`
    }
  }

  const formattedDomains =
    chosenDomains === null
      ? t('allDomains')
      : chosenDomains.length === 1
      ? chosenDomains[0].address
      : (() => {
          const displayed = chosenDomains
            .slice(0, 3)
            .map((d) => d.address)
            .join(', ')
          const remaining = chosenDomains.length - 3
          return remaining > 0 ? `${displayed} +${remaining} more` : displayed
        })()

  return (
    <PDFHeaderWrapper>
      <PDFHeaderTitleSection>
        <PDFHeaderTitle>
          {t('pdf.header.title')}
          <PDFHeaderDateSection>
            {dateRange.fromDate && dateRange.toDate
              ? formatDateRange(dateRange.fromDate, dateRange.toDate, locale)
              : ''}
          </PDFHeaderDateSection>
        </PDFHeaderTitle>
        <LogoWrapper>
          {accountLogoDownloadLink ? renderLogo() : <CheqEssentialsLogo width="250px" />}
        </LogoWrapper>
      </PDFHeaderTitleSection>

      <PDFDomainsSection>{formattedDomains}</PDFDomainsSection>
    </PDFHeaderWrapper>
  )
}
